// External libraries
import { Component, createRef } from "react";

// FontAwesome icons

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { filterBy, orderBy } from "@progress/kendo-data-query";
import { FieldWrapper } from "@progress/kendo-react-form";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { validators } from "../../Form/validators";
import { Invisible, getEntriesByTemplateId, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import { allDatasets } from "../../Common/Store/CommonSignals";
import {
  disabledSections,
  entryValues,
  formErrors,
  template,
} from "../../Entry/Manage/EditEntrySignals";

export class CheckboxGroupField extends Component {
  inputRef = createRef();

  constructor({
    field,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    eventIndex,
    labelVisible,
    value,
    galleryParentName,
    isStageField,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    this.state = {
      value: value,
      valid: this.validate(value, properties?.IsRequired),
      parentFieldValue: galleryParentName
        ? entryValues.value?.[galleryParentName]?.[eventIndex]?.[properties?.ParentFieldName]
        : entryValues.value[properties?.ParentFieldName],
      options: this.optionsHelper(properties?.ParentFieldName, properties?.Dataset?.id),
      exclusionFilterData: this.getConnectionData({ Data: properties?.ExclusionFilter }),
      invisible: false,
      disabled: false,
      touched: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.isRequired = properties?.IsRequired;
    this.isReadOnly = properties?.IsReadOnly;
    this.displayNameHorizontally = properties?.DisplayNameHorizontally;
    this.galleryParentName = galleryParentName;
    this.parentFieldName = properties?.ParentFieldName;
    this.datasetId = properties?.Dataset?.id;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.ExclusionFilterValue = properties?.ExclusionFilterValue;
    this.visibilityRules = properties?.VisibilityRules;
    this.parameterValues = properties?.ParameterValues;
    this.labelVisible = labelVisible;
    this.label = properties?.Label || `${fieldName}:`;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.handleFieldChange = handleFieldChange;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.validationMessage = "Error: A selection is required.";
    this.disposeValidationEffect = () => {};
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  //#region lifecycle functions
  componentDidMount() {
    if (this.visibilityRules || this.parentFieldName) this.handleDependencyEffect();

    // Filter this.state.options based on the exclusion filter value and the exclusion filter data
    if (this.ExclusionFilterValue) {
      this.setState((prevState) => ({
        options: prevState?.options?.filter(
          (option) =>
            !prevState?.exclusionFilterData?.some(
              (exclusionFilterOption) =>
                exclusionFilterOption?.[this.ExclusionFilterValue] === option
            )
        ),
      }));
    }
    if (this.isRequired) {
      if (!this.validate(this.state.value, this.isRequired)) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
      this.handleValidationEffect();
      this.updateFormErrors();
    }

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
    this.state.value.split(", ").forEach((val) => {
      if (!this.state.options.includes(val) && val !== "") {
        this.setState((prevState) => ({ options: [...prevState.options, val] }));
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.value !== nextState.value ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled ||
      JSON.stringify(this.state.options) !== JSON.stringify(nextState.options) ||
      this.state.parentFieldValue !== nextState.parentFieldValue ||
      this.state.touched !== nextState.touched
    );
  }

  getSnapshotBeforeUpdate(_, prevState) {
    return {
      visibilityChange: prevState.invisible && !this.state.invisible,
      valueChange: prevState.value !== this.state.value,
    };
  }

  componentDidUpdate(_, __, snapshot) {
    if (snapshot.visibilityChange) this.setDefaultValue();
    if (this.state.invisible) this.clearField();
    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({ valid: this.validate(prevState.value, this.isRequired) }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.state.disabled) this.clearFormErrors();
  }
  //#endregion lifecycle functions

  //#region custom functions
  validate(value, isRequired) {
    return isRequired ? validators.required(value) : true;
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      const newOptions = this.optionsHelper(this.parentFieldName, this.datasetId);
      const newParentFieldValue = entryValues.value?.[this.galleryParentName]?.[this.eventIndex]?.[
        this.parentFieldName
      ]
        ? entryValues.value?.[this.galleryParentName]?.[this.eventIndex]?.[this.parentFieldName]
        : entryValues.value[this.parentFieldName];

      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
        options: newOptions,
      });
      if (newParentFieldValue !== this.state.parentFieldValue) {
        this.setState(
          (prevState) => ({
            parentFieldValue: newParentFieldValue,
            // Only clear the value if it's no longer valid and not intentionally set to empty
            value:
              newOptions?.includes(prevState.value) || prevState.value === ""
                ? prevState.value
                : "",
          }),
          () => {
            if (this.state.value === "") {
              this.onChange({ value: "" });
            }
          }
        );
      }
    });
  }

  clearField() {
    this.clearFormErrors();
    this.setState({ value: "" });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: "" },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  onChange(e) {
    if (!this.isPrintPreview && !this.isReadOnly) {
      const newValue = e.value ?? []; // Treat null as empty string

      this.setState(
        {
          valid: this.validate(e.value, this.isRequired),
          value: e.value,
          touched: true,
        },
        () => {
          this.updateFormErrors();
          this.handleFieldChange({
            e: { ...e, target: { ...e.target, name: this.fieldName, value: newValue } },
            sectionIndex: this.sectionIndex,
            fieldIndex: this.fieldIndex,
            fieldType: this.fieldType,
            eventIndex: this.eventIndex,
          });
          if (this.isStageField) validateStageStep();
        }
      );
    }
  }

  optionsHelper(parentFieldName, datasetId) {
    const parentFieldValue = entryValues.value?.[this.galleryParentName]?.[this.eventIndex]?.[
      this.parentFieldName
    ]
      ? entryValues.value?.[this.galleryParentName]?.[this.eventIndex]?.[this.parentFieldName]
      : entryValues.value[this.parentFieldName];
    if (parentFieldName) {
      return allDatasets.value
        .find((x) => x.id === datasetId)
        ?.values.filter((option) => parentFieldValue === option.filterValue)
        .map((option) => option.value);
    } else {
      return allDatasets.value
        .find((x) => x.id === datasetId)
        ?.values?.map((option) => option.value);
    }
  }

  getConnectionData = ({ Data }) => {
    switch (Data?.type) {
      case "Template":
        return orderBy(filterBy(getEntriesByTemplateId(Data.id), Data.filter), Data.sort);
      case "Global Parameters":
      case "List":
        return orderBy(
          filterBy(allDatasets.value.find((dataset) => dataset.id === Data.id).values, Data.filter),
          Data.sort
        );
      default:
        return [];
    }
  };

  getClassName() {
    if (this.isRequired) {
      if (this.state.valid) {
        return "!k-valid";
      } else {
        return "!k-invalid";
      }
    }
  }

  //#endregion custom functions

  render() {
    if (this.state.invisible) return null;
    return (
      <Ripple>
        <div
          ref={this.inputRef}
          className="k-d-flex k-flex-col"
        >
          <FieldWrapper>
            <div
              className={this.displayNameHorizontally ? "k-d-flex k-align-items-center k-mt-5" : ""}
            >
              {/* Label to the left */}
              {this.labelVisible && (
                <Label
                  editorId={this.fieldName}
                  editorValid={this.isRequired ? this.state.valid : true}
                  style={{ marginRight: "8px", fontWeight: "bold" }}
                >
                  {this.label}
                </Label>
              )}

              {/* Input Container */}
              <div
                className="k-form-field-wrap"
                style={{ flex: 1 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "start",
                    gap: "8px",
                    flexDirection: "column",
                    marginTop: "4px",
                  }}
                >
                  {this.state.options.map((option, i) => (
                    <Checkbox
                      data-testid={this.fieldName}
                      className={this.getClassName()}
                      key={option + "_" + i}
                      id={`${this.fieldName}_${option}`}
                      name={this.fieldName}
                      value={option}
                      checked={
                        this.state.value ? this.state.value.split(", ").includes(option) : false
                      }
                      label={option}
                      onChange={(e) => {
                        let newValueArray = this.state.value ? this.state.value.split(", ") : [];
                        if (e.target.value) {
                          newValueArray.push(option);
                        } else {
                          newValueArray = newValueArray.filter((val) => val !== option);
                        }
                        const newEvent = { ...e, value: newValueArray.join(", ") };
                        this.onChange(newEvent);
                      }}
                    />
                  ))}
                  <If
                    condition={
                      this.fieldInformation !== undefined &&
                      this.fieldInformation !== "" &&
                      this.fieldInformation !== "<p></p>"
                    }
                  >
                    <TooltipPopupIcon content={this.fieldInformation} />
                  </If>
                </div>
              </div>
            </div>
            <If condition={this.hint}>
              <Hint>{this.hint}</Hint>
            </If>
            <If condition={!this.state.valid && this.state.touched}>
              <Error>{this.validationMessage}</Error>
            </If>
          </FieldWrapper>
        </div>
      </Ripple>
    );
  }
}
